// Helpers para normalizar los errores del backend turnero.
//
// El back devuelve varios wrappers segun status HTTP:
//
//   500 — Excepcion de negocio custom (.NET):
//     { message, exceptionMessage, exceptionType: "Bpba...<Tipo>Exception", solucionable: true, stackTrace }
//     `solucionable: true` = mostrar exceptionMessage.
//
//   500 — Business via System.Exception (quoted — bug viejo del back):
//     { exceptionMessage: "\"Su sesión ha expirado\"", exceptionType: "System.Exception" }
//     El back wrappea con `throw new Exception($"\"{msg}\"")` → JSON-encoded.
//
//   500 — Business via System.Exception (unquoted — patron mas comun):
//     { exceptionMessage: "No se puede reprogramar...", exceptionType: "System.Exception" }
//     El back wrappea con `throw new Exception(msg)` directo.
//
//   500 — Excepcion generica (bug interno .NET):
//     { exceptionMessage: "Object reference not set to an instance of an object." }
//     Texto autogenerado del CLR. NO mostrar — es un bug, no un mensaje al user.
//
//   400 — Validacion framework:
//     { message: "The request is invalid.", messageDetail: "..." }
//     Sin exceptionMessage → fallback.
//
// Politica: default = mostrar `exceptionMessage` si existe; filtramos solo
// los mensajes tecnicos conocidos del framework. Preferimos que el user
// vea un mensaje de negocio nuevo (aunque no lo hayamos previsto) antes
// que ocultarselo detras del fallback generico.

export const DEFAULT_ERROR = 'Ocurrió un error, intentá nuevamente';

// Mensajes autogenerados del framework (.NET/HTTP) que NUNCA deben llegar al
// user. Match por `startsWith` lower-case contra el exceptionMessage.
const KNOWN_TECH_MESSAGES = [
  'object reference not set to an instance',
  'an error has occurred',
  'an unhandled exception',
  'nullreferenceexception',
  'the request is invalid',
  'internal server error',
  'arithmetic operation resulted in an overflow',
  'index was outside the bounds of the array',
  'value cannot be null',
  'invalidoperationexception',
];

// Legacy: el back solia wrappear algunos business como `"msg"` (JSON-encoded).
// Lo mantenemos para no romper esos casos que todavia pueden aparecer.
export const isQuotedBusinessMessage = (msg: unknown): msg is string =>
  typeof msg === 'string' && msg.length >= 2 && msg.startsWith('"') && msg.endsWith('"');

const isKnownTechMessage = (msg: string): boolean => {
  const lower = msg.toLowerCase().trim();
  return KNOWN_TECH_MESSAGES.some((t) => lower.startsWith(t));
};

export const isPresentableError = (data: any): boolean => {
  if (!data || typeof data !== 'object' || typeof data.exceptionMessage !== 'string') {
    return false;
  }
  const stripped = data.exceptionMessage.replace(/^"+|"+$/g, '').trim();
  if (!stripped) return false;
  if (isKnownTechMessage(stripped)) return false;
  return true;
};

export const extractApiError = (err: any): string => {
  const d = err?.response?.data;
  if (!isPresentableError(d)) return DEFAULT_ERROR;
  const clean = d.exceptionMessage.replace(/^"+|"+$/g, '').trim();
  return clean || DEFAULT_ERROR;
};

// Devuelve el `exceptionMessage` raw sin filtrar por `solucionable`. Sirve
// para checks programaticos (ej: detectar el texto "reprogramado" en un
// error de negocio) — no para pintar mensaje en la UI. Lee el data actual
// y, si el interceptor lo vacio, el `originalData` que guardo antes.
export const getRawExceptionMessage = (err: any): string => {
  const data = err?.response?.data ?? err?.response?.originalData;
  return typeof data?.exceptionMessage === 'string' ? data.exceptionMessage : '';
};
